import React from 'react';
import { keyBy, values as _values } from 'lodash';
import { toast } from 'react-toastify';
import { format as formatDate } from 'date-fns';

import ModalButton from './ModalButton';
import ModelFormModal from './modals/ModelFormModal';
import { mapOrderImportRow } from '../shared/models/orderImport';
import { generateOrderId } from '../shared/util';
import { prefectures } from '../shared/config';

// mapOrderImportRow は都道府県を名称→コード変換するため value は名称で持つ
const prefectureOptions = _values(prefectures).map(_ => ({ label: _, value: _ }));

const splitCodes = _ => (_ || '').split(',').map(_ => _.trim()).filter(_ => _);

// 受注取込（CSV）の項目を手入力で1件登録するためのフォーム定義
const fields = ({ products = [] } = {}) => {
  const codes = products.filter(_ => _.code).map(_ => _.code);
  return {
    id: {
      label: '受注番号',
      type: 'string',
      hint: '空の場合は自動で採番',
    },
    name: {
      label: '注文者名',
      type: 'string',
      validations: { required: _ => !!_ },
    },
    email: {
      label: 'メールアドレス',
      type: 'string',
      validations: { required: _ => !!_ },
    },
    phone: {
      label: '電話番号',
      type: 'string',
      validations: { required: _ => !!_ },
    },
    postalCode: {
      label: '郵便番号',
      type: 'string',
      validations: { required: _ => !!_ },
    },
    prefecture: {
      label: '都道府県',
      type: 'select',
      options: prefectureOptions,
      validations: { required: _ => !!_ },
    },
    city: {
      label: '市区町村',
      type: 'string',
      validations: { required: _ => !!_ },
    },
    address: {
      label: '住所',
      type: 'string',
      validations: { required: _ => !!_ },
    },
    productCodes: {
      label: '商品コード',
      type: 'string',
      hint: '複数の場合はカンマ区切り。存在する商品コードのみ',
      validations: {
        required: _ => splitCodes(_).length > 0,
        // 入力されたコードがすべて商品マスタに存在すること
        allExist: _ => splitCodes(_).every(code => codes.includes(code)),
      },
    },
    quantities: {
      label: '数量',
      type: 'string',
      hint: '商品コードと同じ順序・同じ個数でカンマ区切り',
      validations: {
        required: _ => splitCodes(_).length > 0,
        // 商品コードと数量の個数が一致し、すべて正の整数であること
        matchesProductCodes: (_, s) => splitCodes(_).length === splitCodes(s.productCodes).length,
        allPositiveIntegers: _ => splitCodes(_).every(q => /^\d+$/.test(q) && parseInt(q, 10) > 0),
      },
    },
    createdAt: {
      label: '注文日',
      type: 'date',
    },
    note: {
      label: '備考',
      type: 'text',
    },
    skipsCammacs: {
      label: 'CAM MACSに連携しない',
      type: 'boolean',
      initialValue: true,
      hint: 'チェックすると出荷指示（CAM MACS）の連携対象から外れます',
    },
  };
};

export default function OrderFormButton (props) {
  const { label = '受注手動登録', products = [], processOrderImportRows, ...extraProps } = props;
  const onSubmit = async (values, { onClickClose }) => {
    try {
      // mapOrderImportRow はCSV由来の文字列（注文日は yyyy/MM/dd 文字列）を前提とするため合わせる
      const row = mapOrderImportRow(
        {
          ...values,
          // 受注番号が空なら createOrder と同じ方式で自動採番
          id: values.id || await generateOrderId(),
          createdAt: values.createdAt ? formatDate(values.createdAt, 'yyyy/MM/dd') : null,
        },
        keyBy(products.filter(_ => _.code), 'code'),
      );
      // mapOrderImportRow は route/note を返さないため明示的に付与（route は手動登録固定）
      await processOrderImportRows([{
        ...row,
        route: '手動登録',
        note: values.note || null,
        // 手動登録は合計金額を0円固定（明細の price は商品マスタ価格のまま）
        amount: 0,
        // CAM MACSに連携しない場合は cammacsStatus を 'initial' 以外にして getOrders の対象から外す
        ...(values.skipsCammacs && { cammacsStatus: 'none' }),
      }]);
      toast.success('登録しました');
      onClickClose();
    } catch(e) {
      console.error(e);
      toast.error('失敗しました');
    }
  };

  return (
    <ModalButton
      color="secondary"
      label={label}
      Modal={ModelFormModal}
      modalProps={{ title: label, fields: () => fields({ products }), onSubmit, submitLabel: '登録する', hasSpin: true }}
      {...extraProps}
    />
  );
};
